import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable, of, take, tap } from 'rxjs';
import { AgencyService } from 'src/app/services/agency.service';
import { AppPermissions } from 'src/app/shared/app.permissions';
import { ManualFTSIDComponent } from 'src/app/shared/components/manual-ftsid/manual-ftsid.component';
import { AuthorizationModel } from 'src/app/shared/models/authorization-model';
import { EtraceService } from 'src/app/services/etrace.service';

@Component({
  selector: 'ness-crime-gun-firearm',
  templateUrl: './crime-gun-firearm.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./crime-gun-firearm.component.scss'],
  providers: [DialogService]
})
export class CrimeGunFirearmComponent implements OnDestroy {
  @Input() states: ServerModels.ENESS_REF_State[];
  @Input() displayLegacyGunID: boolean;
  @Output() closeFirearmView = new EventEmitter();
  @Output() refreshFirearm = new EventEmitter();
  @Output() onFirearmUpdate = new EventEmitter<ServerModels.GunDetailCustomFields>();
  @Output() refreshTraceData = new EventEmitter();

  readonly canAddFTSID: AuthorizationModel = AppPermissions.canAddTraceData;
  readonly canViewFTS: AuthorizationModel = AppPermissions.canViewTraceData;

  crimeGunForm: FormGroup = this.fb.group({
    FirearmCrimeGunID: '',
    FirearmID: '',
    CrimeGunNumberID: '',
    ObliteratedSerialNumber: false,
    AmendedSerialNumber: '',
    IsStolenGun: null,
    DateStolen: null,
    StateID: '',
    IsFirearmRegistered: null,
    RegistrationDate: null,
    RegistrationStateID: '',
    RegistrationNumber: ['', Validators.maxLength(80)],
    AgencyID: { value: '', disabled: true },
    AgencyName: '',
    LECaseNumber: '',
    Comments: ''
  });
  maxDateRule: Date = new Date();
  filteredAgencies$: Observable<ServerModels.ENESS_REF_Agency[]> = of([]);
  filteredPartners: ServerModels.ENESS_LEPartner[] = [];
  caseNumbers: ServerModels.ENESS_XREF_LECaseNumbers[];

  private manualFtsIdRef: DynamicDialogRef = null;

  private _gunDetailCustomFields: ServerModels.GunDetailCustomFields;
  get gunDetailCustomFields(): ServerModels.GunDetailCustomFields {
    return this._gunDetailCustomFields;
  }
  @Input() set gunDetailCustomFields(customFields: ServerModels.GunDetailCustomFields) {
    this._gunDetailCustomFields = customFields;
    this.populateFormData();
  }


  constructor(public dialogService: DialogService,
    private route: ActivatedRoute,
    private agencyService: AgencyService,
    private etraceService: EtraceService,
    private fb: FormBuilder) { }

  ngOnDestroy(): void {
    if (this.manualFtsIdRef) {
      this.manualFtsIdRef.close();
    }
    this.refreshFirearm.emit();
  }

  public onSubmit(): void {
    if (this.crimeGunForm.valid) {
      const formModel = this.crimeGunForm.getRawValue();
      this._gunDetailCustomFields = { ...this._gunDetailCustomFields, ...formModel };
      this.onFirearmUpdate.emit(this._gunDetailCustomFields);
    }
  }

  private populateFormData(): void {
    if (this._gunDetailCustomFields) {
      if (!!this._gunDetailCustomFields.StateID) {
        let agencyCtrl = this.crimeGunForm.get('AgencyID');
        this.filteredAgencies$ = this.agencyService.getAgenciesForState(this._gunDetailCustomFields.StateID).pipe(tap(
          (agencies: ServerModels.ENESS_REF_Agency[]) => {
            agencyCtrl.enable();
            agencyCtrl.setValue(this._gunDetailCustomFields.AgencyID || '');
          }
        ));
      }

      this.crimeGunForm.setValue({
        FirearmCrimeGunID: this._gunDetailCustomFields.FirearmInformationID || null,
        FirearmID: this._gunDetailCustomFields.FirearmID || null,
        CrimeGunNumberID: this.route.snapshot.paramMap.get('id') ? this.route.snapshot.paramMap.get('id') : this._gunDetailCustomFields.CrimeGunNumberID || null,
        ObliteratedSerialNumber: this._gunDetailCustomFields.ObliteratedSerialNumber || null,
        AmendedSerialNumber: this._gunDetailCustomFields.AmendedSerialNumber || null,
        DateStolen: this._gunDetailCustomFields.DateStolen == null ? null : new Date(this._gunDetailCustomFields.DateStolen),
        IsStolenGun: this._gunDetailCustomFields.IsStolenGun == null ? null : this._gunDetailCustomFields.IsStolenGun,
        LECaseNumber: this._gunDetailCustomFields.LECaseNumber || null,
        StateID: this._gunDetailCustomFields.StateID || '',
        AgencyID: this._gunDetailCustomFields.AgencyID || '',
        AgencyName: this._gunDetailCustomFields.AgencyName || null,
        Comments: this._gunDetailCustomFields.Comments || null,
        IsFirearmRegistered: null,
        RegistrationDate: null,
        RegistrationStateID: null,
        RegistrationNumber: null
      });
    }
  }

  public toggleStolenGunFields(): void {
    this.crimeGunForm.patchValue({
      DateStolen: null,
      AgencyID: this.crimeGunForm.get('IsStolenGun').value == true ? '' : null,
      AgencyName: null,
      StateID: this.crimeGunForm.get('IsStolenGun').value == true ? '' : null,
      LECaseNumber: null,
      Comments: null
    });

    this.filteredAgencies$ = of([]);
    this.crimeGunForm.get('AgencyID').disable();
  }

  public onSelectState(stateId: string) {
    if (!stateId) {
      this.filteredAgencies$ = of([]).pipe(tap((x: ServerModels.ENESS_REF_Agency[]) => this.crimeGunForm.get('AgencyID').disable()));
    } else {
      this.filteredAgencies$ = this.agencyService.getAgenciesForState(stateId).pipe(tap((x: ServerModels.ENESS_REF_Agency[]) => this.crimeGunForm.get('AgencyID').enable()));
    }

    this.crimeGunForm.patchValue({
      'AgencyID': '',
      'AgencyName': ''
    });
  }

  public onSelectAgency(target: EventTarget) {
    let agency: HTMLSelectElement = <HTMLSelectElement>target;
    let agencyCtrl = this.crimeGunForm.get('AgencyName');
    if (!agency.value) {
      agencyCtrl.setValue('');
    } else {
      agencyCtrl.setValue(agency.options[agency.selectedIndex].text);
    }
  }

  public showManualFTSID(): void {
      this.manualFtsIdRef = this.dialogService.open(ManualFTSIDComponent, {
      header: 'Manual FTS ID',
      dismissableMask: true,
      contentStyle: {
        'overflow-y': 'auto',
        'overflow-x': 'hidden',
        'max-height': '80vh',
        'min-height': '20vh',
      },
      styleClass: 'w-sm-75 w-lg-50',
      data: {
        firearmId: this._gunDetailCustomFields.FirearmID
      },
    });

    this.manualFtsIdRef.onClose.pipe(take(1)).subscribe(() => {
      this.refreshTraceData.emit();
    });
  }

  public canRemove(row: ServerModels.FirearmTraceDataViewModel): boolean {
    return row.FTSType === 'T' && !row.IsAutomatch;
  }

  public removeFTSID(row: ServerModels.FirearmTraceDataViewModel): void {
    this.etraceService.removeTraceData(this._gunDetailCustomFields.FirearmID, row.FTSID).pipe(take(1)).subscribe(
      (n:number) => {
        let idx = this._gunDetailCustomFields.FirearmTraceData.indexOf(row);
        this._gunDetailCustomFields.FirearmTraceData.splice(idx, 1);
        this.populateFormData();
      });
  }

  public toggleFirearmRegistration(): void {
    this.crimeGunForm.patchValue({
      RegistrationDate: null,
      RegistrationStateID: this.crimeGunForm.get('IsFirearmRegistered').value == true ? '' : null,
      RegistrationNumber: null
    });
  }
}
